import { initialProducts } from './products'
import { getLegacyStoreData, hasLegacyStoreData } from './store'
import {
  SupabaseRequestError,
  encodeStoragePath,
  getPublicStorageUrl,
  getSupabaseProjectUrl,
  supabaseFetch,
} from './supabase'
import {
  AdminSettings,
  AvailabilityBooking,
  AvailabilityData,
  BlockedDate,
  Booking,
  BookingStatus,
  MigrationStatus,
  Product,
  ProductVariant,
} from './types'

const PRODUCT_IMAGES_BUCKET = 'product-images'
const BOOKING_IMAGES_BUCKET = 'booking-images'

interface ProductRow {
  id: string
  name: string
  description: string | null
  dimensions: string | null
  age_restriction: string | null
  capacity: string | null
  usage_terms: string | null
  application: string
  base_price: number
  variants: ProductVariant[] | null
  delivery_price: number
  image: string
  images: string[] | null
  category: string
  is_active: boolean
  display_order: number | null
}

interface BookingRow {
  id: string
  customer_name: string
  phone: string
  email: string | null
  event_address: string
  product_id: string
  product_name: string
  date: string
  end_date: string | null
  start_time: string
  end_time: string
  total_price: number
  include_delivery: boolean
  note: string | null
  location_image_path: string | null
  status: BookingStatus
  created_at: string
  selected_variant: string | null
}

interface BlockedDateRow {
  id: string
  product_id: string | null
  date: string
  end_date: string | null
  start_time: string | null
  end_time: string | null
  reason: string | null
}

interface SettingsRow {
  id: string
  terms_text: string
  contact_name: string
  contact_phone: string
  contact_email: string
  emailjs_public_key: string | null
  emailjs_service_id: string | null
  emailjs_template_id: string | null
  emailjs_notification_email: string | null
}

interface PublicSettingsRow {
  terms_text: string
  contact_name: string
  contact_phone: string
  contact_email: string
  emailjs_public_key: string | null
  emailjs_service_id: string | null
  emailjs_template_id: string | null
  emailjs_notification_email: string | null
}

interface SignedUrlResponse {
  signedURL: string
}

interface AvailabilityRow {
  entry_type: 'booking' | 'blocked'
  product_id: string | null
  date: string
  end_date: string | null
  status: 'pending' | 'confirmed' | null
}

const defaultSettings: AdminSettings = {
  termsText: '',
  contactInfo: { name: '', phone: '', email: '' },
  emailJs: {
    publicKey: '',
    serviceId: '',
    templateId: '',
    notificationEmail: '',
  },
}

export const loadProducts = async (): Promise<Product[]> => {
  const response = await supabaseFetch('/rest/v1/products?select=*')
  return sortProductsByDisplayOrder(
    ((await response.json()) as ProductRow[]).map(mapProductRow),
  )
}

export const loadProduct = async (id: string): Promise<Product | null> => {
  const response = await supabaseFetch(
    `/rest/v1/products?select=*&id=eq.${encodeURIComponent(id)}&limit=1`,
  )
  const rows = (await response.json()) as ProductRow[]
  return rows[0] ? mapProductRow(rows[0]) : null
}

export const createProduct = async (product: Product): Promise<Product> => {
  const response = await supabaseFetch(
    '/rest/v1/products',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Prefer: 'return=representation',
      },
      body: JSON.stringify(toProductRow(product)),
    },
    { requireAuth: true },
  )
  const rows = (await response.json()) as ProductRow[]
  if (!rows[0]) throw new Error('Proizvod nije spremljen.')
  return mapProductRow(rows[0])
}

export const updateProduct = async (
  id: string,
  product: Product,
): Promise<Product> => {
  const response = await supabaseFetch(
    `/rest/v1/products?id=eq.${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Prefer: 'return=representation',
      },
      body: JSON.stringify(toProductRow(product)),
    },
    { requireAuth: true },
  )
  const rows = (await response.json()) as ProductRow[]
  if (!rows[0]) throw new Error('Proizvod nije pronađen.')
  return mapProductRow(rows[0])
}

export const saveProductOrder = async (
  products: Product[],
): Promise<void> => {
  await supabaseFetch(
    '/rest/v1/rpc/set_product_order',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: products.map((product, index) => ({
          id: product.id,
          display_order: index,
        })),
      }),
    },
    { requireAuth: true },
  )
}

export const deleteProduct = async (id: string): Promise<void> => {
  await supabaseFetch(
    `/rest/v1/products?id=eq.${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    { requireAuth: true },
  )
}

export const uploadProductImage = async (file: File): Promise<string> => {
  const path = createStoragePath('products', file)
  await supabaseFetch(
    `/storage/v1/object/${PRODUCT_IMAGES_BUCKET}/${encodeStoragePath(path)}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
        'x-upsert': 'false',
      },
      body: file,
    },
    { requireAuth: true },
  )
  return getPublicStorageUrl(PRODUCT_IMAGES_BUCKET, path)
}

export const loadBookingsForAdmin = async (): Promise<Booking[]> => {
  const response = await supabaseFetch(
    '/rest/v1/bookings?select=*&order=created_at.desc',
    {},
    { requireAuth: true },
  )
  const bookings = ((await response.json()) as BookingRow[]).map(mapBookingRow)
  return Promise.all(
    bookings.map(async (booking) => ({
      ...booking,
      locationImage: booking.locationImagePath
        ? await getPrivateLocationImageUrl(booking.locationImagePath)
        : undefined,
    })),
  )
}

export const createBooking = async (
  booking: Booking,
  locationImageFile?: File | null,
): Promise<Booking> => {
  let locationImagePath: string | undefined
  if (locationImageFile)
    locationImagePath = await uploadBookingLocationImage(locationImageFile)

  try {
    const response = await supabaseFetch(
      '/rest/v1/rpc/create_booking_request',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payload: toBookingRow({ ...booking, locationImagePath }),
        }),
      },
    )
    const rows = (await response.json()) as BookingRow[]
    if (!rows[0]) throw new Error('Rezervacija nije spremljena.')
    return mapBookingRow(rows[0])
  } catch (error) {
    if (locationImagePath)
      await deleteBookingLocationImage(locationImagePath).catch(() => undefined)
    throw error
  }
}

export const updateBookingStatus = async (
  id: string,
  status: BookingStatus,
): Promise<void> => {
  await supabaseFetch(
    `/rest/v1/bookings?id=eq.${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    },
    { requireAuth: true },
  )
}

export const deleteBooking = async (booking: Booking): Promise<void> => {
  await supabaseFetch(
    `/rest/v1/bookings?id=eq.${encodeURIComponent(booking.id)}`,
    { method: 'DELETE' },
    { requireAuth: true },
  )
  if (booking.locationImagePath)
    await deleteBookingLocationImage(booking.locationImagePath)
}

export const loadAvailability = async (): Promise<AvailabilityData> => {
  const response = await supabaseFetch('/rest/v1/rpc/get_public_availability', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  })
  const rows = (await response.json()) as AvailabilityRow[]
  return {
    bookings: rows
      .filter((row) => row.entry_type === 'booking' && Boolean(row.product_id))
      .map(
        (row): AvailabilityBooking => ({
          productId: row.product_id as string,
          date: row.date,
          endDate: row.end_date || undefined,
          status: row.status === 'pending' ? 'pending' : 'confirmed',
        }),
      ),
    blockedDates: rows
      .filter((row) => row.entry_type === 'blocked')
      .map((row) => ({
        id: `availability-${row.product_id || 'all'}-${row.date}-${row.end_date || ''}`,
        productId: row.product_id || undefined,
        date: row.date,
        endDate: row.end_date || undefined,
      })),
  }
}

export const loadBlockedDates = async (): Promise<BlockedDate[]> => {
  const response = await supabaseFetch(
    '/rest/v1/blocked_dates?select=*&order=date.asc',
    {},
    { requireAuth: true },
  )
  return ((await response.json()) as BlockedDateRow[]).map(mapBlockedDateRow)
}

export const createBlockedDate = async (
  block: BlockedDate,
): Promise<BlockedDate> => {
  const response = await supabaseFetch(
    '/rest/v1/blocked_dates',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Prefer: 'return=representation',
      },
      body: JSON.stringify(toBlockedDateRow(block)),
    },
    { requireAuth: true },
  )
  const rows = (await response.json()) as BlockedDateRow[]
  if (!rows[0]) throw new Error('Blokada nije spremljena.')
  return mapBlockedDateRow(rows[0])
}

export const deleteBlockedDate = async (id: string): Promise<void> => {
  await supabaseFetch(
    `/rest/v1/blocked_dates?id=eq.${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    { requireAuth: true },
  )
}

export const loadSettings = async (): Promise<AdminSettings> => {
  const response = await supabaseFetch(
    '/rest/v1/admin_settings?select=*&id=eq.main&limit=1',
    {},
    { requireAuth: true },
  )
  const rows = (await response.json()) as SettingsRow[]
  return rows[0] ? mapSettingsRow(rows[0]) : defaultSettings
}

export const loadPublicSettings = async (): Promise<AdminSettings> => {
  const response = await supabaseFetch('/rest/v1/rpc/get_public_settings', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  })
  const rows = (await response.json()) as PublicSettingsRow[]
  if (!rows[0]) return defaultSettings
  return {
    termsText: rows[0].terms_text || '',
    contactInfo: {
      name: rows[0].contact_name || '',
      phone: rows[0].contact_phone || '',
      email: rows[0].contact_email || '',
    },
    emailJs: {
      publicKey: rows[0].emailjs_public_key || '',
      serviceId: rows[0].emailjs_service_id || '',
      templateId: rows[0].emailjs_template_id || '',
      notificationEmail: rows[0].emailjs_notification_email || '',
    },
  }
}

export const saveSettings = async (
  settings: AdminSettings,
): Promise<AdminSettings> => {
  const response = await supabaseFetch(
    '/rest/v1/admin_settings?on_conflict=id',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Prefer: 'resolution=merge-duplicates,return=representation',
      },
      body: JSON.stringify(toSettingsRow(settings)),
    },
    { requireAuth: true },
  )
  const rows = (await response.json()) as SettingsRow[]
  if (!rows[0]) throw new Error('Postavke nisu spremljene.')
  return mapSettingsRow(rows[0])
}

export const getMigrationStatus = async (): Promise<MigrationStatus> => {
  const [products, bookings, blockedDates, settings] = await Promise.all([
    countRows('products'),
    countRows('bookings'),
    countRows('blocked_dates'),
    countRows('admin_settings'),
  ])
  return {
    remoteIsEmpty: products + bookings + blockedDates + settings === 0,
    legacyHasData: hasLegacyStoreData(),
    remoteCounts: { products, bookings, blockedDates, settings },
  }
}

export const migrateLegacyData = async (): Promise<{
  products: number
  bookings: number
  blockedDates: number
}> => {
  const status = await getMigrationStatus()
  const { remoteCounts } = status
  if (
    remoteCounts.products > 0 &&
    remoteCounts.bookings > 0 &&
    remoteCounts.blockedDates > 0
  ) {
    throw new Error(
      'Prijenos je dostupan samo dok su Supabase tablice prazne.',
    )
  }

  const legacy = getLegacyStoreData()
  const products =
    remoteCounts.products > 0
      ? []
      : legacy.products.length > 0
        ? legacy.products
        : initialProducts
  const settings = legacy.settings

  if (products.length > 0) {
    const productRows = await Promise.all(
      products.map(async (product) => {
        const image = await migrateLegacyProductImage(product.image)
        const images = await Promise.all(
          (product.images || []).map(migrateLegacyProductImage),
        )
        return toProductRow({ ...product, image, images })
      }),
    )
    await insertBatch('products', productRows)
  }
  if (remoteCounts.bookings === 0 && legacy.bookings.length > 0) {
    const bookingRows = await Promise.all(
      legacy.bookings.map(async (booking) => {
        const locationImagePath = booking.locationImage?.startsWith('data:')
          ? await uploadBookingLocationImage(
              dataUrlToFile(booking.locationImage),
            )
          : undefined
        return toBookingRow({ ...booking, locationImagePath })
      }),
    )
    await insertBatch('bookings', bookingRows)
  }
  if (remoteCounts.blockedDates === 0 && legacy.blockedDates.length > 0) {
    await insertBatch(
      'blocked_dates',
      legacy.blockedDates.map(toBlockedDateRow),
    )
  }
  if (remoteCounts.settings === 0 && hasSettingsContent(settings))
    await saveSettings(settings)

  return {
    products: products.length,
    bookings: remoteCounts.bookings === 0 ? legacy.bookings.length : 0,
    blockedDates:
      remoteCounts.blockedDates === 0 ? legacy.blockedDates.length : 0,
  }
}

export const getSupabaseErrorMessage = (error: unknown): string => {
  if (error instanceof SupabaseRequestError) {
    const details = error.message.toLowerCase()
    if (error.status === 401)
      return 'Vaša administratorska prijava je istekla. Prijavite se ponovno.'
    if (error.status === 403)
      return 'Nemate dopuštenje za ovu radnju. Provjerite RLS pravila i administratorski račun u SUPABASE_SETUP.md.'
    if (
      error.status === 404 ||
      details.includes('does not exist') ||
      details.includes('relation')
    ) {
      return 'Supabase tablice još nisu postavljene. U SQL Editoru pokrenite cijelu skriptu iz SUPABASE_SETUP.md.'
    }
    return `Supabase se ne može povezati: ${error.message}`
  }
  return error instanceof Error
    ? error.message
    : 'Dogodila se neočekivana greška.'
}

const countRows = async (table: string): Promise<number> => {
  const response = await supabaseFetch(`/rest/v1/${table}?select=id`, {
    method: 'HEAD',
    headers: { Prefer: 'count=exact' },
  })
  const range = response.headers.get('content-range')
  const count = range?.split('/').pop()
  return count && count !== '*' ? Number(count) : 0
}

const insertBatch = async (
  table: string,
  rows: Record<string, unknown>[],
): Promise<void> => {
  if (!rows.length) return
  await supabaseFetch(
    `/rest/v1/${table}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Prefer: 'return=minimal' },
      body: JSON.stringify(rows),
    },
    { requireAuth: true },
  )
}

const uploadBookingLocationImage = async (file: File): Promise<string> => {
  const path = createStoragePath('venues', file)
  await supabaseFetch(
    `/storage/v1/object/${BOOKING_IMAGES_BUCKET}/${encodeStoragePath(path)}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
        'x-upsert': 'false',
      },
      body: file,
    },
  )
  return path
}

const getPrivateLocationImageUrl = async (path: string): Promise<string> => {
  const response = await supabaseFetch(
    `/storage/v1/object/sign/${BOOKING_IMAGES_BUCKET}/${encodeStoragePath(path)}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ expiresIn: 3600 }),
    },
    { requireAuth: true },
  )
  const result = (await response.json()) as SignedUrlResponse
  if (!result.signedURL)
    throw new Error('Nije moguće otvoriti privatnu sliku lokacije.')
  return result.signedURL.startsWith('http')
    ? result.signedURL
    : `${getSupabaseProjectUrl()}/storage/v1${result.signedURL.startsWith('/') ? '' : '/'}${result.signedURL}`
}

const deleteBookingLocationImage = async (path: string): Promise<void> => {
  await supabaseFetch(
    `/storage/v1/object/${BOOKING_IMAGES_BUCKET}/${encodeStoragePath(path)}`,
    { method: 'DELETE' },
    { requireAuth: true },
  )
}

const mapProductRow = (row: ProductRow): Product => ({
  id: row.id,
  name: row.name,
  description: row.description || undefined,
  dimensions: row.dimensions || undefined,
  ageRestriction: row.age_restriction || undefined,
  capacity: row.capacity || undefined,
  usageTerms: row.usage_terms || undefined,
  application: row.application,
  basePrice: Number(row.base_price),
  variants: row.variants || undefined,
  deliveryPrice: Number(row.delivery_price),
  image: row.image,
  images: row.images || undefined,
  category: row.category,
  isActive: row.is_active,
  displayOrder: row.display_order ?? undefined,
})

const toProductRow = (product: Product): Record<string, unknown> => ({
  id: product.id,
  name: product.name.trim(),
  description: textOrNull(product.description),
  dimensions: textOrNull(product.dimensions),
  age_restriction: textOrNull(product.ageRestriction),
  capacity: textOrNull(product.capacity),
  usage_terms: textOrNull(product.usageTerms),
  application: product.application,
  base_price: product.basePrice,
  variants: product.variants?.length ? product.variants : null,
  delivery_price: product.deliveryPrice,
  image: product.image,
  images: product.images?.length ? product.images : null,
  category: product.category,
  is_active: product.isActive !== false,
  display_order: product.displayOrder ?? null,
})

const mapBookingRow = (row: BookingRow): Booking => ({
  id: row.id,
  customerName: row.customer_name,
  phone: row.phone,
  email: row.email || '',
  eventAddress: row.event_address,
  productId: row.product_id,
  productName: row.product_name,
  date: row.date,
  endDate: row.end_date || undefined,
  startTime: row.start_time,
  endTime: row.end_time,
  totalPrice: Number(row.total_price),
  includeDelivery: row.include_delivery,
  note: row.note || undefined,
  locationImagePath: row.location_image_path || undefined,
  status: row.status,
  createdAt: row.created_at,
  selectedVariant: row.selected_variant || undefined,
})

const toBookingRow = (booking: Booking): Record<string, unknown> => ({
  id: booking.id,
  customer_name: booking.customerName.trim(),
  phone: booking.phone.trim(),
  email: textOrNull(booking.email),
  event_address: booking.eventAddress.trim(),
  product_id: booking.productId,
  product_name: booking.productName,
  date: booking.date,
  end_date: booking.endDate || null,
  start_time: booking.startTime,
  end_time: booking.endTime,
  total_price: booking.totalPrice,
  include_delivery: booking.includeDelivery,
  note: textOrNull(booking.note),
  location_image_path: booking.locationImagePath || null,
  status: booking.status,
  created_at: booking.createdAt,
  selected_variant: textOrNull(booking.selectedVariant),
})

const mapBlockedDateRow = (row: BlockedDateRow): BlockedDate => ({
  id: row.id,
  productId: row.product_id || undefined,
  date: row.date,
  endDate: row.end_date || undefined,
  startTime: row.start_time || undefined,
  endTime: row.end_time || undefined,
  reason: row.reason || undefined,
})

const toBlockedDateRow = (block: BlockedDate): Record<string, unknown> => ({
  id: block.id,
  product_id: block.productId || null,
  date: block.date,
  end_date: block.endDate || null,
  start_time: block.startTime || null,
  end_time: block.endTime || null,
  reason: textOrNull(block.reason),
})

const mapSettingsRow = (row: SettingsRow): AdminSettings => ({
  termsText: row.terms_text || '',
  contactInfo: {
    name: row.contact_name || '',
    phone: row.contact_phone || '',
    email: row.contact_email || '',
  },
  emailJs: {
    publicKey: row.emailjs_public_key || '',
    serviceId: row.emailjs_service_id || '',
    templateId: row.emailjs_template_id || '',
    notificationEmail: row.emailjs_notification_email || '',
  },
})

const toSettingsRow = (settings: AdminSettings): Record<string, unknown> => ({
  id: 'main',
  terms_text: settings.termsText,
  contact_name: settings.contactInfo.name,
  contact_phone: settings.contactInfo.phone,
  contact_email: settings.contactInfo.email,
  emailjs_public_key: settings.emailJs?.publicKey || null,
  emailjs_service_id: settings.emailJs?.serviceId || null,
  emailjs_template_id: settings.emailJs?.templateId || null,
  emailjs_notification_email: settings.emailJs?.notificationEmail || null,
})

const createStoragePath = (folder: string, file: File): string =>
  `${folder}/${new Date().toISOString().slice(0, 10)}/${createId()}.${getFileExtension(file)}`

const createId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

const getFileExtension = (file: File): string => {
  const fromName = file.name.split('.').pop()?.toLowerCase()
  if (fromName && /^[a-z0-9]{1,10}$/.test(fromName)) return fromName
  const fromType = file.type.split('/')[1]?.toLowerCase()
  return fromType && /^[a-z0-9]{1,10}$/.test(fromType) ? fromType : 'jpg'
}

const textOrNull = (value?: string): string | null => value?.trim() || null

const migrateLegacyProductImage = async (value: string): Promise<string> =>
  value.startsWith('data:') ? uploadProductImage(dataUrlToFile(value)) : value

const dataUrlToFile = (dataUrl: string): File => {
  const [metadata, encoded] = dataUrl.split(',')
  if (!metadata || !encoded)
    throw new Error('Stara slika lokacije nije ispravna.')
  const mimeType = metadata.match(/data:([^;]+)/)?.[1] || 'image/jpeg'
  const binary = atob(encoded)
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0))
  return new File(
    [bytes],
    `legacy-location.${mimeType.split('/')[1] || 'jpg'}`,
    {
      type: mimeType,
    },
  )
}

const hasSettingsContent = (settings: AdminSettings): boolean =>
  Boolean(
    settings.termsText ||
      settings.contactInfo.name ||
      settings.contactInfo.phone ||
      settings.contactInfo.email,
  )

const sortProductsByDisplayOrder = (products: Product[]): Product[] => {
  const catalogOrder = new Map(
    initialProducts.map((product, index) => [product.id, index]),
  )

  return [...products].sort((first, second) => {
    if (
      first.displayOrder !== undefined &&
      second.displayOrder !== undefined
    )
      return first.displayOrder - second.displayOrder
    if (first.displayOrder !== undefined) return -1
    if (second.displayOrder !== undefined) return 1

    const firstIndex = catalogOrder.get(first.id)
    const secondIndex = catalogOrder.get(second.id)
    if (firstIndex !== undefined && secondIndex !== undefined)
      return firstIndex - secondIndex
    if (firstIndex !== undefined) return -1
    if (secondIndex !== undefined) return 1
    return first.name.localeCompare(second.name, 'hr')
  })
}
